import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../utils/apiClient";
import { needsHostedCheckout } from "../utils/hostedCheckout";

const POLL_MS = 4000;

/**
 * useHostedCheckout — pay a platform-billed amount in a new tab on the
 * platform's own domain, and find out here when it went through.
 *
 *   const checkout = useHostedCheckout({ onPaid: refresh });
 *   if (checkout.required) return checkout.start("/billing/hosted-checkout", { planId, billingCycle });
 *   …
 *   <HostedCheckoutModal checkout={checkout} />
 *
 * See utils/hostedCheckout for WHEN this is needed. The tab is opened inside
 * the click, before the request: a `window.open` after an `await` is no longer
 * a user gesture and most browsers block it. If it is blocked anyway, the modal
 * offers a plain link, which always opens.
 *
 * This tab then polls the session (every few seconds while visible, and at once
 * on focus — which is when the payer comes back). The server answers from the
 * record, the webhook or Razorpay itself, so a payment whose new tab was closed
 * mid-way still shows up here.
 *
 * Phases: idle · opening · waiting · blocked · paid · expired · closed · error
 */
export default function useHostedCheckout({ onPaid } = {}) {
  const [state, setState] = useState({ phase: "idle" });
  const [checking, setChecking] = useState(false);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const inFlight = useRef(false);

  const start = useCallback((startPath, body = {}) => {
    let tab = null;
    try {
      tab = window.open("", "_blank");
    } catch {
      tab = null;
    }
    if (tab) {
      try {
        tab.opener = null;
        tab.document.title = "Opening secure payment…";
        tab.document.body.textContent = "Opening the secure payment page…";
      } catch {
        // A browser that refuses to let us write into the blank tab still lets
        // us navigate it, which is all that matters.
      }
    }
    setState({ phase: "opening" });

    apiClient
      .post(startPath, body)
      .then(({ data }) => {
        const { session, url } = data;
        if (tab && !tab.closed) {
          tab.location.replace(url);
          setState({ phase: "waiting", session, url });
        } else {
          setState({ phase: "blocked", session, url });
        }
      })
      .catch((err) => {
        if (tab && !tab.closed) tab.close();
        setState({
          phase: "error",
          message: err?.response?.data?.message || "Could not start the payment. Please try again.",
        });
      });
  }, []);

  const sessionId = state.session?.id;
  const polling = !!sessionId && (state.phase === "waiting" || state.phase === "blocked");

  const check = useCallback(async () => {
    if (!sessionId || inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await apiClient.get(`/hosted-checkout/sessions/${sessionId}`);
      const session = data.session;
      if (session.state === "paid") {
        setState((prev) => ({ ...prev, phase: "paid", session }));
        onPaidRef.current?.(session);
      } else if (session.state === "expired" || session.state === "closed") {
        setState((prev) => ({ ...prev, phase: session.state, session }));
      } else {
        setState((prev) => ({ ...prev, session }));
      }
    } catch {
      // A failed poll is not a failed payment. The next one will tell.
    } finally {
      inFlight.current = false;
    }
  }, [sessionId]);

  const checkNow = useCallback(async () => {
    setChecking(true);
    await check();
    setChecking(false);
  }, [check]);

  useEffect(() => {
    if (!polling) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") await check();
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") check();
    };
    timer = setTimeout(tick, POLL_MS);
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [polling, check]);

  const dismiss = useCallback(() => setState({ phase: "idle" }), []);

  return {
    required: needsHostedCheckout(),
    start,
    checkNow,
    checking,
    dismiss,
    ...state,
  };
}
